import React, {
  FunctionComponent,
  ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import {
  Card,
  Layout,
  Collapse,
  Input,
  Button,
  Tabs,
  TabPanel,
  notification,
  Icon,
} from 'tea-component';

import {
  useBoolean,
  useDebounceEffect,
  useDebounceFn,
  useLocalStorageState,
  useMemoizedFn,
  useMount,
  useSafeState,
  useSetState,
  useThrottleFn,
  useTimeout,
} from 'ahooks';
import { find, get, isEmpty } from 'lodash-es';
import Loading from '@/pages/intelligence/components/Loading';
import TranslatorSelect from '../GPTTranslator/components/TranslatorSelect';
import FormUpload from '../ResourceUpload/components/FormUpload';
import { useAigcIntl } from '../../utils/utils';
import {
  useGetSummarizeGoal,
  useGetSummarizeUrl,
  useGetSummarizeVisualize,
  useGetVisualizeExplain,
} from './hooks';
import { useSearchParamsState, useUrlQueryParam } from '../../hooks/useQuery';
import DefauletUpload from '@/pages/aigc/assets/svg/defaulet-upload.svg';
import './index.scss';
import {
  Evaluate,
  Explain,
  Recommend,
  Refine,
} from './components/VisualizationTags';
import { CodePromptDisplay } from './components/CodePromptDisplay';
import {
  DataSummaryCardTypeView,
  GoalExplorationCardTypeView,
} from './components/CardList';
import { getAuthorization } from '@/utils/utils';
import { NoDataRefresh } from './components/NoDataRefresh';
import ConfirmModal from '../Text2SQL/components/RelevantTable/components/ConfirmModal';
import { useModalOpen } from '../Text2SQL/components/RelevantTable/hoooks/useModal';
import SvgIcon from '@/pages/home/components/SvgIcon';
import CardSlot from '@/pages/intelligence/components/CardSlot';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import {useCachedState} from '../../hooks/useCachedState';
const { Content } = Layout;
interface Props {
  [key: string]: unknown;
}
interface IUploadFiles<P = (...args: any[]) => void> {
  urlAsync: P;
  urlMutate: P;
  urlData: unknown[];
  isEn: Boolean;
  formatMessage?: P | any;
  setTrue: P;
  setFalse: P;
  isShow: Boolean;
  visualizeLoading: boolean;
  visualizeData: unknown[];
  goalData: unknown[];
  visualizeAsync: P;
  library: string;
  mutate: P;
  refreshAsync: P;
  urlLoading: Boolean;
  goalLoading: Boolean;
}
type IArrayOptions = Array<{
  value: string;
  text: string;
}>;

const listCard: IArrayOptions = [
  {
    value: 'stocks.csv',
    text: 'stocks.csv',
  },
  {
    value: 'cars.json',
    text: 'cars.json',
  },
  {
    value: 'wheat.json',
    text: 'wheat.json',
  },
  {
    value: 'movies.json',
    text: 'movies.json',
  },
  {
    value: 'seattle-weather.csv',
    text: 'seattle-weather.csv',
  },
  {
    value: 'sp500.csv',
    text: 'sp500.csv',
  },
  {
    value: 'la-riots.csv',
    text: 'la-riots.csv',
  },
  {
    value: 'airports.csv',
    text: 'airports.csv',
  },
];
const options: typeof listCard = [
  {
    value: 'altair',
    text: 'Altair',
  },
  {
    value: 'matplotlib',
    text: 'Matplotlib',
  },
  {
    value: 'seaborn',
    text: 'Seaborn',
  },
  {
    value: 'ggplot',
    text: 'Ggplot',
  },
];
export const commonParams: Record<
'textgen_config',
{ [key in string]: unknown }
> = {
  textgen_config: {
    temperature: 0,
    n: 1,
    model: 'gpt-3.5-turbo-0301',
    max_tokens: null,
    provider: 'openai',
  },
};
const DataAnalysis: FunctionComponent<Props> = (): ReactElement => {
  // i18n
  const { formatMessage, isEn } = useAigcIntl('dataAnalysis');
  // 下拉以及各类options值集中在这个状态维护
  const [optionsValue, setOptionsValue] = useSetState<{
    [key in string]: unknown;
  }>({
    optionsVisualizationValue: 'seaborn',
  });
  const [isShow, { toggle, setTrue, setFalse }] = useBoolean(true);
  const [catchTime, setCatchTime, resetCache] = useCachedState<string>( 'db_lida_start_catch_time', '0');
  // 需要链式调用Promise，ahooks暂没发现这样的api,
  // TODO:采用useCreateReducer以及useContext结合，透传Props下去
  const {
    data: urlData,
    loading: urlLoading,
    runAsync: urlAsync,
    mutate: urlMutate,
    cancel: urlCancel,
  } = useGetSummarizeUrl();
  const {
    data: goalData,
    loading: goalLoading,
    cancel: goalCancel,
    mutate: goalMutate,
  } = useGetSummarizeGoal(
    useMemo(
      () => ({
        ...commonParams,
        summary: urlData?.summary || {},
        lang: isEn ? 'en' : 'zh',
      }),
      [urlData, isEn, ],
    ),
  );
  const {
    data: visualizeData,
    loading: visualizeLoading,
    runAsync: visualizeAsync,
    mutate: visualizeMutate,
    cancel: visualizeCancel,
    refreshAsync,
  } = useGetSummarizeVisualize(
    useMemo(
      () => ({
        summary: urlData?.summary || {},
        goal: goalData?.data?.[0] || {},
        library: optionsValue.optionsVisualizationValue,
        lang: isEn ? 'en' : 'zh',
        loading: goalLoading,
        ...commonParams,
      }),
      [
        isEn,
        urlData?.summary,
        goalData,
        optionsValue.optionsVisualizationValue,
        goalLoading,
      ],
    ),
  );
  // 清空数据以及取消正在请求的promise,用于文件上传取消，如果用户在上传过程中，点击取消重新上传，会导致上次的请求还存在，
  // 这里，直接取消，包括用户离开当前路由的时候，直接对数据以及promise进行清空，防止下次进来的时候，留存着缓存数据
  const handleUserLeaveCancelPromiseAndCancelData = useMemoizedFn((isClear = false) => {
    if (isClear) {
      visualizeMutate([]);
      urlMutate([]);
      goalMutate([]);
    }
    goalCancel();
    visualizeCancel();
    urlCancel();
  });


  useEffect(() => {
    const lifeTime = 24 * 60 * 60 * 1000;
    // 当前时间  > 存入时间+过期时间
    if (Date.now() > catchTime + lifeTime) {
      console.log('缓存过期了');
      window.localStorage.setItem('db_lida_file_type','');
      handleUserLeaveCancelPromiseAndCancelData(true);
     
      setCatchTime(0);
    }
  }, [catchTime, handleUserLeaveCancelPromiseAndCancelData, setCatchTime]);

  useEffect(() => {
    return () => {
      //  重置缓存时间
      if (!catchTime) {
        setCatchTime(Date.now());
      }
      handleUserLeaveCancelPromiseAndCancelData();
    };
  }, [catchTime, handleUserLeaveCancelPromiseAndCancelData, isShow, setCatchTime,]);

  useMount(()=>{
    buriedPointPage({
      pageId: '700801',
      pageName: 'DataAnalysis',
      extInfo: '',
      pagePath: '/DataAnalysis',
      typeId: 'aigc',
    });
  });
  return (
    <Content>
      <Content.Header
        title={formatMessage('title')}
        operation={
          <TranslatorSelect
            style={{ width: 310 }}
            label="aigc_dataAnalysis_visualization"
            value={optionsValue.optionsVisualizationValue}
            options={options}
            onConfirm={(optionsVisualizationValue: string) => {
              setOptionsValue({
                optionsVisualizationValue,
              });
              visualizeAsync({
                library: optionsVisualizationValue,
              });
            }}
          />
        }
      />
      {/* <AppProvider>
      </AppProvider> */}
      <Content.Body full className={'dbi-DataAnalysis-content-body'}>
        <UploadFilesToForm
          {...{
            urlAsync,
            urlData: urlData?.summary,
            urlMutate,
            isEn,
            formatMessage,
            setTrue,
            setFalse,
          }}
        />
        {isShow && (urlLoading ||
          goalLoading || !isEmpty(urlData?.summary)) && (
          <>
            <DataSummaryCardList
              {...{
                urlData: urlData?.summary,
                goalData: goalData?.data,
                urlLoading,
                goalLoading,
                visualizeAsync,
                formatMessage,
              }}
            />
            <VisualizationGenerationView
              {...{
                visualizeLoading: goalLoading || visualizeLoading,
                visualizeData: visualizeData?.charts,
                goalData: goalData?.data,
                visualizeAsync,
                urlData: urlData?.summary,
                library: optionsValue.optionsVisualizationValue,
                mutate: visualizeMutate,
                isEn,
                formatMessage,
                refreshAsync,
              }}
            />
          </>
        )}
      </Content.Body>
    </Content>
  );
};

export default DataAnalysis;
export const UploadFilesToForm = ({
  urlAsync,
  urlMutate,
  isEn,
  formatMessage,
  setTrue,
  setFalse,
}: Partial<IUploadFiles>) => {
 
  const [loadingUpload, setLoadingUpload] = useState<string | undefined>('');
  const [isActive, setIsActive, resetCache] = useCachedState<string>( 'db_lida_file_type', '');
  // 文件类型回调
  const { run: handleSelectFilesType } = useDebounceFn(
    (optionsfilesValue: string) => {
      if (isActive === optionsfilesValue) {
        setIsActive('');
        return;
      }
      setIsActive(optionsfilesValue);
      urlAsync({
        url: `https://raw.githubusercontent.com/vega/vega-datasets/next/data/${optionsfilesValue}`,
        lang: isEn ? 'en' : 'zh',
      });
      setTrue?.();
    },
    { wait: 300 },
  );

  const UploadDefaultCopy = () => {
    return (
      <div className="upload-container">
        <img src={DefauletUpload} alt="" />
        <div className="upload-container-defaulet">
          {formatMessage('click_or_drag')} <br />
          {formatMessage('upload_type')}
        </div>
      </div>
    );
  };
  return (
    <>
      <Card className={'dbi-DataAnalysis-card '}>
        <Card.Body>
          <div className="dbi-DataAnalysis-title" style={{ marginTop: '0px' }}>
            {formatMessage('ready')}
          </div>
          <div className="form-upload">
            <FormUpload
              action="/api/v1/lida/summarize"
              accept={[
                'application/json',
                'text/csv',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel',
              ]}
              tips={'drag_tips2'}
              headers={() => {
                const token = getAuthorization();
                return {
                  Authorization: token,
                };
              }}
              fileSize={1024 * 1024 *100}
              isShowImg
              CustomizeElementContent={<UploadDefaultCopy />}
              handleExternalonStart={(res) => {
                setFalse();
                setLoadingUpload('validating');
              }}
              handleExternalonCancel={() => {
                setLoadingUpload('');
              }}
              onSuccess={(res) => {
                if (res.status) {
                  urlMutate(res);
                  setTrue();
                } else {
                  notification.error({
                    description: res.message,
                    duration: 2000,
                  });
                }
                setLoadingUpload('');
              }}
            />
          </div>
          <div className="dbi-DataAnalysis-title">
            {formatMessage('don_have_data')}
          </div>

          <div className="dbi-DataAnalysis-list-container">
            {listCard.map((task) => (
              <div
                className={classNames('dbi-DataAnalysis-list', {
                  'dbi-DataAnalysis-list-active': isActive === task.value,
                })}
                key={task.value}
                onClick={() => handleSelectFilesType(task.value)}
              >
                {task.text}
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>
      {loadingUpload === 'validating' && (
        <CardSlot iconList={[]}>
          <div style={{ height: '600px' }}>
            <Loading />
          </div>
        </CardSlot>
      )}
    </>
  );
};

export const DataSummaryCardList = ({
  urlData,
  urlLoading,
  goalLoading,
  goalData,
  visualizeAsync,
  formatMessage,
}: Partial<IUploadFiles>) => {
  const { modalDetailStat, detailClone, detailopen } = useModalOpen();
  return (
    <Card className={'dbi-DataAnalysis-card'}>
      <Card.Body title={formatMessage('data_summary')}>
        <div className="dbi-DataAnalysis-mark-text">
          {formatMessage('an_enriched')}
        </div>
        <div className="dbi-DataAnalysis-summary-container">
          {urlLoading ? (
            <div style={{ height: '200px' }}>
              <Loading />
            </div>
          ) : (
            <Collapse
              defaultActiveIds={['1']}
              icon={(active) => (
                <SvgIcon
                  type={`arrow-down`}
                  style={{
                    transform: active ? '' : 'rotate(-90deg)',
                    transition: 'all .3s',
                  }}
                />
              )}
            >
              <Collapse.Panel id="1" title={urlData?.file_name || '-'}>
                <DataSummaryCardTypeView
                  {...{ data: urlData?.fields, formatMessage }}
                />
              </Collapse.Panel>
            </Collapse>
          )}
        </div>
        <div
          className="dbi-DataAnalysis-mark-text summary-modal-text"
          onClick={detailopen}
        >
          {formatMessage('view_raw_summary')}
        </div>
        <div className="dbi-DataAnalysis-defaulet-text">
          {formatMessage('goal_exploration')}
        </div>
        <div className="dbi-DataAnalysis-mark-text">
          {formatMessage('automatically')}
        </div>
        <div className="dbi-DataAnalysis-summary-container">
          {urlLoading || goalLoading ? (
            <div style={{ height: '200px' }}>
              <Loading />
            </div>
          ) : (
            <Collapse
              defaultActiveIds={['1']}
              icon={(active) => (
                <SvgIcon
                  type={`arrow-down`}
                  style={{
                    transform: active ? '' : 'rotate(-90deg)',
                    transition: 'all .3s',
                  }}
                />
              )}
            >
              <Collapse.Panel
                id="1"
                title={
                  (goalData?.length > 1 &&
                    `${formatMessage('goals')} (${goalData?.length}) `) ||
                  '-'
                }
              >
                <GoalExplorationCardTypeView
                  {...{ data: goalData, visualizeAsync }}
                />
              </Collapse.Panel>
            </Collapse>
          )}
        </div>
      </Card.Body>
      <ConfirmModal
        {...{
          visible: modalDetailStat,
          caption: formatMessage('raw_dataset_summary'),
          click: detailClone,
          clone: detailClone,
          size: 800,
        }}
      >
        <div className="desc">{formatMessage('dictionary_below')}</div>
        <CodePromptDisplay
          {...{ code: JSON.stringify(urlData, null, '\t'), mode: 'json' }}
        ></CodePromptDisplay>
      </ConfirmModal>
    </Card>
  );
};

export const VisualizationGenerationView = ({
  visualizeLoading,
  visualizeData,
  visualizeAsync,
  goalData,
  urlData,
  library,
  mutate,
  isEn,
  formatMessage,
  refreshAsync,
}: Partial<IUploadFiles>) => {
  const [text, setText] = useState('');
  const [text2, setText2] = useState('');

  const [activeId, setActiveId] = useState('1');
  const ix =window.localStorage.getItem('db_lida_goal_type')? JSON.parse(window.localStorage.getItem('db_lida_goal_type') || '') : '0';
  const [params, setParams] = useSetState({});

  const {
    data,
    loading,
    mutate: explainMutate,
    refreshAsync: expleanRefresh,
  } = useGetVisualizeExplain(
    activeId,
    useMemo(
      () => ({
        ...params,
        lang: isEn ? 'en' : 'zh',
      }),
      [isEn, params],
    ),
  );
  const messagesEndRef = useRef(null);

  useDebounceEffect(
    () => {
      const question = get(
        find(goalData, (_, index) => index === +ix),
        'question',
        goalData?.[0]?.question,
      );
      // TODO:待排查，这里会保留上一次的参数，会覆盖默认的，导致请求数据异常
      // 因为ahooks机制,对数据有缓存功能，包括参数，都会缓存，导致使用异步runAsync,并且使用manual为false ，参数会冲突，正常使用clearCatch,把数据以及参数缓存清空的，这里直接重新merge promise,
      visualizeAsync?.({
        goal: find(goalData, (_, index) => index === +ix),
        summary: urlData,
      });
      setText(question);
      setText2(question);
    },
    [goalData, ix, isEn, urlData],
    {
      wait: 300,
    },
  );

  useEffect(() => {
    if (activeId !== '1') {
      scrollToBottom();
    }
  }, [activeId, data, loading]);

  useEffect(() => {
    setActiveId('1');
    explainMutate([]);
  }, [library, ix]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const tabs = [
    {
      id: '1',
      label: formatMessage('refine'),
      component: Refine,
    },
    {
      id: '2',
      label: formatMessage('explain'),
      component: Explain,
    },
    {
      id: '3',
      label: formatMessage('evaluate'),
      component: Evaluate,
    },
    {
      id: '4',
      label: formatMessage('recommend'),
      component: Recommend,
    },
  ];
  const configTitle = useMemo(() => {
    return (
      {
        2: ['generate_an_explanation', 'evaluate_the_chart'],
        3: ['generate_self_evaluation', 'evaluate_the_chart'],
        4: ['generate_similar_charts', 'generate_recommendations'],
      }[activeId] ?? ''
    );
  }, [activeId]);
  const hanldeRefreshPromise = () => {
    refreshAsync();
  };

  const handleOnChangeStencil = async () => {
    await visualizeAsync?.({
      goal: {
        index: 0,
        question: text,
        visualization: text,
        rationale: ' ',
      },
    });
    setText2(text);
  };
  const { run: handleActionsFetchChart } = useDebounceFn(
    () => {
      const params = {
        3: {
          goal: find(goalData, (_, index) => index === +ix),
        },
        4: {
          summary: urlData || {},
        },
      }[activeId];
      setParams({
        ...commonParams,
        ...params,
        library,
        code: visualizeData?.[0]?.code,
      });
    },
    {
      wait: 500,
    },
  );
  return (
    <Card className={'dbi-DataAnalysis-card'}>
      <Card.Body title={formatMessage('visualization_generation')}>
        <div className="dbi-DataAnalysis-mark-text">
          {formatMessage('select_goal')}
        </div>
        <Input
          className="dbi-DataAnalysis-input"
          value={text}
          onChange={(value, context) => {
            setText(value);
          }}
          placeholder=""
          onPressEnter={handleOnChangeStencil}
        />
        <Button className="submit" onClick={handleOnChangeStencil}>
          {formatMessage('generate')}
        </Button>

        <div className="dbi-DataAnalysis-visualization-container">
          {visualizeLoading ? (
            <div style={{ height: '300px', margin: 'auto' }}>
              <Loading />
            </div>
          ) : (
            visualizeData?.map((task) => (
              <>
                <div className="dbi-DataAnalysis-visualization-container-left">
                  <div className="dbi-DataAnalysis-title pading_left_20">
                    {text2}
                  </div>
                  <NoDataRefresh
                    {...{
                      hanldeRefreshPromise,
                      refReshLoaidng: visualizeLoading,
                      imgSrc: task?.raster,
                    }}
                  />
                </div>
                <div className="dbi-DataAnalysis-visualization-container-right">
                  <div
                    className="dbi-DataAnalysis-title pading_left_20"
                    style={{ marginBottom: '12px' }}
                  >
                    {`</> ${formatMessage('python_code')}`}
                  </div>
                  <div
                    className="dbi-DataAnalysis-mark-text pading_left_20"
                    style={{ marginBottom: '9px' }}
                  >
                    {formatMessage('visualization_created')}
                  </div>
                  <CodePromptDisplay {...{ code: task.code }} />
                </div>
              </>
            ))
          )}
        </div>
        <div className="dbi-DataAnalysis-tabs">
          <Tabs
            tabs={tabs}
            animated={false}
            activeId={activeId}
            onActive={(tab: any) => {
              setActiveId(tab.id);
            }}
          />
        </div>
        {tabs.map((tab) =>
          activeId === tab.id ? (
            <TabPanel id={tab.id} key={tab.id} ref={messagesEndRef}>
              {tab.id === '1' ? (
                <div className="text-refine">
                  {formatMessage('modify_chart')}
                </div>
              ) : (
                <>
                  <div className="text-refine">
                    {formatMessage(configTitle[0])}
                  </div>
                  <Button
                    className="bottom-submit"
                    onClick={handleActionsFetchChart}
                  >
                    {formatMessage(configTitle[1])}
                  </Button>
                </>
              )}
              {loading ? (
                <div style={{ height: '400px' }}>
                  <Loading />
                </div>
              ) : (
                <tab.component
                  {...{
                    goalData,
                    urlData,
                    library,
                    mutate,
                    code: visualizeData?.[0]?.code,
                    data,
                    formatMessage,
                    expleanRefresh,
                  }}
                />
              )}
            </TabPanel>
          ) : (
            ''
          ),
        )}
      </Card.Body>
    </Card>
  );
};
